import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import {
    flightCategoryDescription,
    formatCardinalBearing,
    formatCeiling,
    formatObservationAge,
    formatVisibility,
    formatWind,
    resolveFlightCategory
} from 'components/airspace/WeatherStrip/format';

import type { DroneWeather, FlightCategory } from 'hooks/useAirspaceWeather/types';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const PILL_CLASS: Record<FlightCategory, string> = {
    IFR: styles.pillIfr,
    LIFR: styles.pillLifr,
    MVFR: styles.pillMvfr,
    UNKNOWN: styles.pillUnknown,
    VFR: ''
};

const renderAdvisoryItem = (label: string, ok: boolean, key: string) => (
    <span
        className={`${styles.advisoryItem} ${ok ? styles.advisoryOk : styles.advisoryFail}`}
        key={key}>
        <span className={styles.advisoryMarker}>{ok ? '✓' : '✕'}</span>

        <span>{label}</span>
    </span>
);

const renderDetailRows = (weather: DroneWeather) => {
    const bearing = formatCardinalBearing(weather.windDirection);
    const bearingText = bearing === null ? '' : ` (${bearing})`;
    const detailRows: { label: string; value: string }[] = [
        { label: 'Wind', value: `${formatWind(weather)}${bearingText}` },
        { label: 'Visibility', value: formatVisibility(weather) ?? 'Unreported' },
        { label: 'Ceiling', value: formatCeiling(weather) }
    ];

    if (weather.temperatureC !== null) {
        const dewpointText =
            weather.dewpointC === null ? '' : ` / ${Math.round(weather.dewpointC)}° dew`;

        detailRows.push({
            label: 'Temp',
            value: `${Math.round(weather.temperatureC)}°C${dewpointText}`
        });
    }

    if (weather.altimeterHpa !== null) {
        detailRows.push({
            label: 'Altimeter',
            value: `${weather.altimeterHpa.toFixed(0)} hPa`
        });
    }

    return (
        <Wrapper className={styles.detailsGrid}>
            {detailRows.flatMap(row => [
                <span className={styles.detailLabel} key={`${row.label}-label`}>
                    {row.label}
                </span>,
                <span className={styles.detailValue} key={`${row.label}-value`}>
                    {row.value}
                </span>
            ])}
        </Wrapper>
    );
};

const AirportWeatherReadout: FunctionComponent<Props> = ({
    className = '',
    isExpanded = false,
    showStation = true,
    testId = TEST_ID,
    weather
}) => {
    if (weather.droneWeather === null) {
        return null;
    }

    const { droneWeather } = weather;
    const category = resolveFlightCategory(droneWeather);
    const primaryText = `${formatWind(droneWeather)} · ${formatVisibility(droneWeather) ?? '—'} · ${formatCeiling(droneWeather)}`;
    const ageText = formatObservationAge(droneWeather.observationTime);
    const stationText = showStation
        ? ageText === null
            ? droneWeather.station
            : `${droneWeather.station} · ${ageText}`
        : null;

    return (
        <VerticalContainer className={`${styles.readout} ${className}`} testId={testId}>
            <HorizontalContainer className={styles.summaryRow}>
                <span className={`${styles.pill} ${PILL_CLASS[category]}`}>{category}</span>

                <span className={styles.primary}>{primaryText}</span>

                {stationText === null ? null : (
                    <span className={styles.station}>{stationText}</span>
                )}
            </HorizontalContainer>

            {isExpanded ? (
                <VerticalContainer className={styles.detailsBlock}>
                    <span className={styles.detailsCaption}>
                        {flightCategoryDescription[category]}
                    </span>

                    {renderDetailRows(droneWeather)}

                    <Wrapper className={styles.advisoryRow}>
                        {renderAdvisoryItem('Wind OK', droneWeather.vfrAdvisory.windOk, 'wind')}

                        {renderAdvisoryItem(
                            'Visibility OK',
                            droneWeather.vfrAdvisory.visibilityOk,
                            'visibility'
                        )}

                        {renderAdvisoryItem(
                            'Ceiling OK',
                            droneWeather.vfrAdvisory.ceilingOk,
                            'ceiling'
                        )}
                    </Wrapper>

                    {droneWeather.rawMetar.length === 0 ? null : (
                        <span className={styles.metarRaw}>{droneWeather.rawMetar}</span>
                    )}

                    <span className={styles.disclaimer}>{droneWeather.vfrAdvisory.disclaimer}</span>
                </VerticalContainer>
            ) : null}
        </VerticalContainer>
    );
};

export default AirportWeatherReadout;
